"use client"

import { useEffect } from "react"

/**
 * The hosted chrome script marks a header game as active by matching
 * `window.location.href` against each item's `custom_game_url`. Last Man
 * Standing is still `coming_soon` with an empty URL in the games API, so the
 * script falls back to Games Hub. This app *is* Last Man Standing, so we
 * re-apply the active class after the menu is rendered.
 *
 * `initializeMenu()` fetches the games list asynchronously and then clones
 * `.menuItemsSource` into the desktop `.menuRow`, so we watch the header
 * until those items land.
 */
const LMS_LABEL = /last man standing/i

function isLastManStanding(item: Element) {
  const link = item.querySelector("a")
  const label = `${link?.textContent ?? ""} ${link?.getAttribute("title") ?? ""}`
  return LMS_LABEL.test(label)
}

function markLastManStandingActive() {
  document.querySelectorAll(".smallMenuItem").forEach((item) => {
    const isLms = isLastManStanding(item)
    item.classList.toggle("active", isLms)
    if (isLms) item.classList.remove("disabled")
  })
}

export default function ChromeActiveGame() {
  useEffect(() => {
    let cancelled = false
    const timeouts: number[] = []

    const apply = () => {
      if (cancelled) return
      markLastManStandingActive()
    }

    // `addActiveClass()` runs in the same turn as the desktop clone and only
    // mutates class names, which a childList observer will miss. Re-apply on
    // the next tick so Games Hub does not stay active.
    const applyAndRecheck = () => {
      apply()
      timeouts.push(window.setTimeout(apply, 0))
    }

    applyAndRecheck()

    const root = document.getElementById("site-chrome-header") ?? document.body
    const observer = new MutationObserver(applyAndRecheck)
    observer.observe(root, { childList: true, subtree: true })
    window.addEventListener("load", applyAndRecheck)

    return () => {
      cancelled = true
      observer.disconnect()
      window.removeEventListener("load", applyAndRecheck)
      timeouts.forEach((id) => window.clearTimeout(id))
    }
  }, [])

  return null
}
